import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import './ViewStudents.css';
import ScheduleDemo from './ScheduleDemo';

function ViewSchools({ onClose, onEditSchool }) {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPackage, setFilterPackage] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedSchool, setExpandedSchool] = useState(null);
  const [schedulingDemoFor, setSchedulingDemoFor] = useState(null);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [studentCounts, setStudentCounts] = useState({});

  useEffect(() => {
    loadSchools();
  }, []);

  const loadSchools = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);

      const q = query(
        collection(db, 'schools'),
        where('franchiseeRef', '==', franchiseeRef)
      );

      const snapshot = await getDocs(q);
      const schoolsList = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(school => school.schoolStatus !== 'prospect');

      schoolsList.sort((a, b) => a.schoolName.localeCompare(b.schoolName));
      setSchools(schoolsList);
      
      await loadStudentCounts(schoolsList, franchiseeRef);
    } catch (error) {
      console.error('Error loading schools:', error);
      alert('Error loading schools');
    } finally {
      setLoading(false);
    }
  };

  const loadStudentCounts = async (schoolsList, franchiseeRef) => {
    try {
      const counts = {};
      
      const studentsQuery = query(
        collection(db, 'students'),
        where('franchiseeRef', '==', franchiseeRef),
        where('isActive', '==', true)
      );
      
      const studentsSnapshot = await getDocs(studentsQuery);
      
      studentsSnapshot.docs.forEach(doc => {
        const student = doc.data();
        const schoolId = student.schoolRef?.id;
        if (schoolId) {
          counts[schoolId] = (counts[schoolId] || 0) + 1;
        }
      });
      
      setStudentCounts(counts);
    } catch (error) {
      console.error('Error loading student counts:', error);
    }
  };

  const handleToggleActive = async (schoolId, currentStatus) => {
    if (!window.confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this school?`)) {
      return;
    }

    try {
      await updateDoc(doc(db, 'schools', schoolId), {
        isActive: !currentStatus
      });
      
      loadSchools();
      alert(`School ${currentStatus ? 'deactivated' : 'activated'} successfully!`);
    } catch (error) {
      console.error('Error updating school status:', error);
      alert('Error updating school status');
    }
  };

  const filteredSchools = schools.filter(school => {
    const matchesSearch = school.schoolName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (school.schoolCode || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPackage = filterPackage === 'all' || 
                          (filterPackage === '4-term' && school.schoolFee?.termType === 4) ||
                          (filterPackage === '3-term' && school.schoolFee?.termType === 3) ||
                          (filterPackage === 'custom' && school.packageId === 'custom');

    const matchesStatus = filterStatus === 'all' ||
                         (filterStatus === 'active' && school.isActive) ||
                         (filterStatus === 'inactive' && !school.isActive);

    return matchesSearch && matchesPackage && matchesStatus;
  });

  if (loading) {
    return (
      <div className="view-students-container">
        <div className="loading">Loading schools...</div>
      </div>
    );
  }

  return (
    <div className="view-students-container">
      <div className="view-students-header">
        <div className="header-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1>📋 View Schools</h1>
          <button
            onClick={() => setFiltersCollapsed(!filtersCollapsed)}
            style={{
              background: '#4169E1',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            {filtersCollapsed ? '🔽 Show Filters' : '🔼 Hide Filters'}
          </button>
        </div>

        {!filtersCollapsed && (
          <div style={{ marginTop: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', textTransform: 'uppercase' }}>
                  Fee Package:
                </label>
                <select
                  value={filterPackage}
                  onChange={(e) => setFilterPackage(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="all">All Packages</option>
                  <option value="4-term">4 Term</option>
                  <option value="3-term">3 Term</option>
                  <option value="custom">Custom</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', textTransform: 'uppercase' }}>
                  Status:
                </label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', textTransform: 'uppercase' }}>
                  Search:
                </label>
                <input
                  type="text"
                  placeholder="Name, Account #, Contact..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Total Schools Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px',
        borderRadius: '12px',
        marginTop: '20px',
        marginBottom: '20px'
      }}>
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          display: 'inline-block'
        }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '4px' }}>
            TOTAL SCHOOLS:
          </div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#667eea' }}>
            {filteredSchools.length}
          </div>
        </div>
      </div>

      {/* Schools Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          background: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderRadius: '8px'
        }}>
          <thead>
            <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #ddd' }}>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>
                SCHOOL CODE
              </th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>
                SCHOOL NAME
              </th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>
                STUDENTS
              </th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>
                STATUS
              </th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>
                ACTIONS
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredSchools.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  No schools found matching your filters
                </td>
              </tr>
            ) : (
              filteredSchools.map(school => (
                <React.Fragment key={school.id}>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '16px', fontSize: '14px' }}>
                      {school.schoolCode || 'N/A'}
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', fontWeight: '500' }}>
                      {school.schoolName}
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px' }}>
                      👥 {studentCounts[school.id] || 0}
                    </td>
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: school.isActive ? '#DC2626' : '#666',
                        color: 'white'
                      }}>
                        {school.isActive ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => setExpandedSchool(expandedSchool === school.id ? null : school.id)}
                          style={{
                            padding: '8px 12px',
                            background: '#6B7280',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                          title="View Details"
                        >
                          ▼
                        </button>
                        <button
                          onClick={() => onEditSchool(school)}
                          style={{
                            padding: '8px 12px',
                            background: '#F59E0B',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                          title="Edit School"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => setSchedulingDemoFor(school)}
                          style={{
                            padding: '8px 12px',
                            background: '#10B981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                          title="Schedule Demo"
                        >
                          📅
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedSchool === school.id && (
                    <tr>
                      <td colSpan="5" style={{ padding: '20px', background: '#F9FAFB', borderBottom: '1px solid #e5e7eb' }}>
                        <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600' }}>School Details</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                          <div><strong>Address:</strong> {school.address || 'N/A'}</div>
                          <div><strong>Contact Person:</strong> {school.contactPerson || 'N/A'}</div>
                          <div><strong>Contact Phone:</strong> {school.contactPhone || 'N/A'}</div>
                          <div><strong>Contact Email:</strong> {school.contactEmail || 'N/A'}</div>
                          <div><strong>Lesson Day:</strong> {school.lessonDay || 'Not set'}</div>
                          <div><strong>Lesson Time:</strong> {school.lessonTime || 'Not set'}</div>
                          <div><strong>Term Fee:</strong> R{school.schoolFee?.termFee || 0}</div>
                          <div><strong>Instalment Fee:</strong> R{school.schoolFee?.instalmentFee || 0}</div>
                          <div><strong>Admin Fee:</strong> R{school.adminFee || 0}</div>
                          <div><strong>Package:</strong> {school.packageId || 'N/A'}</div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {schedulingDemoFor && (
        <ScheduleDemo
          school={schedulingDemoFor}
          onClose={() => setSchedulingDemoFor(null)}
          onDemoScheduled={() => {
            setSchedulingDemoFor(null);
            loadSchools();
          }}
        />
      )}
    </div>
  );
}

export default ViewSchools;